import React, {ChangeEvent, useEffect, useState} from "react";
import {Table, TableBody, TableCell, TableContainer, TablePagination, TableRow} from "@material-ui/core";
import {Toolbar, Typography} from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Divider from "@material-ui/core/Divider";
import LinearProgress from "@material-ui/core/LinearProgress";
import DownloadDropDownIconButton from "src/components/common/DownloadDropDownIconButton";
import {downloadCapturesAsZip, downloadTableDataAsCsv} from "src/utils/DownloadUtil";
import {RequestInputData} from "src/model/RequestInputData";
import SortableTableHead from "src/components/common/SortableTableHead";
import { SortingType } from "src/components/common/SortableTableHead";
import { stableSort, getComparator } from "src/components/common/SortableTableHead/tableSort";
import { COLUMN_HEADERS, ERROR_MESSAGE, EMPTY_RESULTS_MESSAGE, INITIAL_MESSAGE } from "./constant";
import { useStyles } from "src/styles/ResultTableStyles";


type ResultsTableProps = {
    itemCapturesData: any[] | undefined,
    inputData: RequestInputData,
    displayProgressBar: boolean,
    displayErrorMessage: boolean,
    setCaptureToDisplay: (capture: any) => void,
    setDisplayProgressBar: (display: boolean) => void
}

const ResultsTable: React.FunctionComponent<ResultsTableProps> = (props: ResultsTableProps) => {
    const styles = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [selectedCapture, setSelectedCapture] = useState("");
    const [sortType, setSortType] = useState<SortingType>("desc");
    const [sortBy, setSortBy] = useState("");

    useEffect(() => {  
        setSelectedCapture("")
        setSortType("desc");
        setPage(0);
        setRowsPerPage(5);
        if (props.itemCapturesData) {
            setSortBy("captureTimestamp");
        }
    },[props.itemCapturesData]);

    const emptyRows = props.itemCapturesData ?
        rowsPerPage - Math.min(rowsPerPage, props.itemCapturesData.length - page * rowsPerPage) : rowsPerPage;

    const handleSelectRow = (captureData: any) => {
        setSelectedCapture(captureData.captureId);
        props.setCaptureToDisplay(captureData);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownloadCaptures = async () => {
        props.setDisplayProgressBar(true);
        await downloadCapturesAsZip(props.itemCapturesData!, props.inputData);
        props.setDisplayProgressBar(false);
    };

    const handleDownloadTableData = () => {
        const sortedTableData = stableSort(props.itemCapturesData!, getComparator(sortType, sortBy));
        const dataRows: any[][] = sortedTableData.map(item =>
            COLUMN_HEADERS.map(col => item[col.property]));
        downloadTableDataAsCsv(dataRows, props.inputData, COLUMN_HEADERS);
    }

    const downloadOptions = [
        {id: "downloadAsZip", label: "Download All Captures (.zip)", onClick: handleDownloadCaptures},
        {id: "downloadAsCsv", label: "Download Table Data (.csv)", onClick: handleDownloadTableData}
    ];

    const handleRequestSort = (property: string) => {
        const head = COLUMN_HEADERS.find(header => {
            return header.property === property;
        });

        if (head && head.sortable) {
            const isDesc = sortBy === property && sortType === "desc";
            setSortType(isDesc ? "asc" : "desc");
            setSortBy(property);
        }
    }

    const generateTableBody = () => {
        if (props.itemCapturesData && props.itemCapturesData.length > 0)
            return (
                <>
                    {stableSort(props.itemCapturesData, getComparator(sortType, sortBy))
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((captureData: any) => (
                            <TableRow
                                key={captureData.captureId}
                                hover={true}
                                selected={captureData.captureId === selectedCapture}
                                onClick={() => handleSelectRow(captureData)}
                            >
                                <TableCell>{captureData.hardwareSource}</TableCell>
                                <TableCell>{captureData.captureId}</TableCell>
                                <TableCell>{captureData.asin}</TableCell>
                                <TableCell>{captureData.fnsku}</TableCell>
                                <TableCell>{captureData.fcsku}</TableCell>
                                <TableCell>{captureData.warehouseId}</TableCell>
                                <TableCell>{captureData.binId}</TableCell>
                                <TableCell>{captureData.captureTimestamp}</TableCell>
                                <TableCell>{(captureData.isCompleteCapture).toString()}</TableCell>
                            </TableRow>
                        ))
                    }
                    {emptyRows > 0 && (
                        <TableRow style={{height: 33 * emptyRows}}>
                            <TableCell colSpan={COLUMN_HEADERS.length}/>
                        </TableRow>
                    )}
                </>
            );
        else
            return (
                <TableRow style={{height: 33 * emptyRows}}>
                    <TableCell colSpan={COLUMN_HEADERS.length} className={styles.tableMessagePlaceholder}>
                        {getMessageToDisplay()}
                    </TableCell>
                </TableRow>
            )
    }; 

    const getMessageToDisplay = () => {
        if (props.itemCapturesData && props.itemCapturesData.length === 0)
            return (<div className={styles.emptyResultsMessageText}>{EMPTY_RESULTS_MESSAGE}</div>);
        else if (!props.itemCapturesData && props.displayErrorMessage)
            return (<div className={styles.errorMessageText}>{ERROR_MESSAGE}</div>);
        else
            return (<div className={styles.initialMessageText}>{INITIAL_MESSAGE}</div>);
    };

    return (
        <div>
            <Paper className={styles.paper}>
                <Toolbar variant="dense">
                    <Typography variant="subtitle1" className={styles.title} component="div">Results</Typography>
                    <div className={styles.flow}></div>
                    <DownloadDropDownIconButton
                        disabled={!(props.itemCapturesData && props.itemCapturesData.length > 0)}
                        options={downloadOptions}/>
                </Toolbar>
                {props.displayProgressBar ? <LinearProgress className={styles.progressBar}/> : <div></div>}
                <Divider></Divider>
                <TableContainer>
                    <Table size="small">
                        <SortableTableHead 
                            columnHeads={COLUMN_HEADERS}
                            sortType={sortType}
                            sortBy={sortBy}
                            onRequestSort={handleRequestSort}/>
                        <TableBody>{generateTableBody()}</TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    className={styles.pagination}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    component="div"
                    count={props.itemCapturesData ? props.itemCapturesData.length : 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </div>
    );
};

export default ResultsTable;